import React from "react";
import { BookmarkIcon as BookmarkIconOutline } from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkIconSolid } from "@heroicons/react/24/solid";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateTopicBookmark,
  useDeleteTopicBookmark,
} from "../api/mutations";
import { BookmarkTypeEnum, ModuleI } from "../types";
import { useGetTopicBookmarks } from "../api/queries";
import Button from "../componentLibrary/Button";

export default function BookmarkButton({
  module,
  width = 5,
  variant = "secondary",
  iconOnly = false,
}: {
  module: ModuleI;
  width?: number;
  variant?: "custom" | "secondary";
  iconOnly?: boolean;
}) {
  const {
    data: bookmarks,
    isError,
    isLoading,
  } = useGetTopicBookmarks(module._id);
  const queryClient = useQueryClient();

  const mutation = useCreateTopicBookmark({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["bookmarks", BookmarkTypeEnum.MODULE],
      });
    },
  });

  const deleteMut = useDeleteTopicBookmark({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["bookmarks", BookmarkTypeEnum.MODULE],
      });
    },
  });

  const iconClassName = iconOnly ? "w-6 h-6" : `w-${width} h-${width}`;

  if (isLoading || isError || !bookmarks)
    return <BookmarkIconOutline color="black" className="h-6 w-6" />;

  const addBookmark = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    mutation.mutate({ module: module._id, type: BookmarkTypeEnum.MODULE });
  };

  const removeBookmark = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    deleteMut.mutate(bookmarks[0]._id);
  };

  if (iconOnly) {
    if (bookmarks.length <= 0)
      return (
        <Button.Circle
          variant="secondary"
          onClick={addBookmark}
          className="p-2 focus:outline-none focus:ring-0"
          aria-label="Add to bookmark"
          title="Add to bookmark"
        >
          <BookmarkIconOutline
            className={`${iconClassName} text-electrum cursor-pointer`}
          />
        </Button.Circle>
      );
    return (
      <Button.Circle
        variant="secondary"
        onClick={removeBookmark}
        className="p-2 focus:outline-none focus:ring-0"
        aria-label="Remove Bookmark"
        title="Remove Bookmark"
      >
        <BookmarkIconSolid
          className={`${iconClassName} text-electrum cursor-pointer`}
        />
      </Button.Circle>
    );
  }

  if (bookmarks.length <= 0)
    return (
      <Button.Circle
        className="p-1.5 hover:bg-electrum-100"
        onClick={addBookmark}
        variant={variant}
      >
        <BookmarkIconOutline
          className={`w-${width} h-${width} text-electrum cursor-pointer`}
        />
      </Button.Circle>
    );

  return (
    <Button.Circle
      className="p-1.5 hover:bg-electrum-100"
      onClick={removeBookmark}
      variant={variant}
    >
      <BookmarkIconSolid
        className={`w-${width} h-${width} text-electrum cursor-pointer`}
      />
    </Button.Circle>
  );
}
